import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { type Facade, type FacadeSpanStratum, type FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import { FloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import type {
  FoundationAssembly,
  FoundationAssemblyOverrides,
} from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { SlabType, type FoundationCondition } from "building-types/foundations/foundationEnums";
import type { FoundationPattern } from "building-types/foundations/foundationPatternTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass, RoomCover, RoomEnclosure, RoomSupport } from "building-types/rooms/roomTypesEnum";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { PathPriority } from "building-types/walls/pathPriority";
import type { WallAssemblyOperator } from "building-types/walls/wallAssemblyOverrideTypes";
import type {
  WallSectionSide,
  WallSection,
  WallDescriptor,
  WallSideRoomDescriptor,
  WallSideDescriptor,
  WallOperators,
} from "building-types/walls/wallDesciptorTypes";
import { StructuralAssemblyType, WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import type { WallSideGeneratedDatums } from "building-types/walls/wallSideGeneratedDatums";
import type { WallPath } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesNew, worldInchesTolevel } from "common/utils/nominalNumbers";
import { floorAssemblyBaseOffset, floorAssemblyFloorPlateThickness } from "doc/elements/floor/floorAssembly";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { docEntityGetSortedChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { isInteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { determineFoundationCondition } from "doc/utils/foundationSystemTools";
import { pathPriorityOpposite } from "doc/utils/wallTools";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { collectAllWallDatums, collectWallFacadeDatums } from "../../utils/facade";
import { roomOpenToAbove, roomOpenToBelow } from "../../utils/roomOpenToBelow";
import { floorPathBaseOffset } from "../floor/floorPathUtils";
import {
  buildFoundationAssemblyComponents,
  foundationAssemblyTop,
  getActiveFoundationAssemblyOverride,
  makeFoundationAssembly,
  makeFoundationPathAssemblyOverride,
} from "../foundation/foundationAssemblyTools";
import { getFoundationPathDatums } from "../foundation/foundationStrip";
import { slabDepthAtRoomClass, slabTypeAtRoomClass } from "../foundation/slabUtils";
import { collectHalfEdgeOperators } from "../wallAssemblies/wallAssemblyOverride";

function wallSectionSideNew(structure: StructuralAssemblyType, cladding: WallCladdingType): WallSectionSide {
  return {
    structure,
    cladding,
  };
}

function wallSectionNew(
  leftSide: WallSectionSide,
  rightSide: WallSectionSide,
  bottomOffset = levelInchesNew(0),
  topOffset = levelInchesNew(0),
  startOffset = 0,
  endOffset = 1,
): WallSection {
  return {
    leftSide,
    rightSide,
    bottomOffset,
    topOffset,
    startOffset,
    endOffset,
  };
}

export function wallSectionOpposite(wallSection: WallSection): WallSection {
  return {
    ...wallSection,
    leftSide: wallSection.rightSide,
    rightSide: wallSection.leftSide,
    startOffset: wallSection.endOffset,
    endOffset: wallSection.startOffset,
  };
}

export function wallDescriptorOpposite(wallDescriptor: WallDescriptor): WallDescriptor {
  return {
    ...wallDescriptor,
    leftSide: wallDescriptor.rightSide,
    rightSide: wallDescriptor.leftSide,
    wallOperators: wallOperatorsOpposite(wallDescriptor.wallOperators),
    floorPriority: pathPriorityOpposite(wallDescriptor.floorPriority),
  };
}

export function wallPathOpposite({
  wallType,
  wallCondition,
  wallDescriptor,
  halfEdgeIndex: halfEdge,
  leftRoom,
  rightRoom,
  leftRoomKind,
  rightRoomKind,
  startX,
  startY,
  endX,
  endY,
  length,
  baseOffset,
}: WallPath): WallPath {
  return {
    wallType,
    wallCondition,
    wallDescriptor: wallDescriptorOpposite(wallDescriptor),
    halfEdgeIndex: planarGraphAdjacentHalfEdge(halfEdge),
    leftRoom: rightRoom,
    rightRoom: leftRoom,
    leftRoomKind: rightRoomKind,
    rightRoomKind: leftRoomKind,
    startX: endX,
    startY: endY,
    endX: startX,
    endY: startY,
    length: length,
    baseOffset,
  };
}

function wallOperatorsOpposite(wallOperators: WallOperators): WallOperators {
  return {
    ...wallOperators,
    leftFinishOperators: wallOperators.rightFinishOperators,
    rightFinishOperators: wallOperators.leftFinishOperators,
    leftCoreOperators: wallOperators.rightCoreOperators,
    rightCoreOperators: wallOperators.leftCoreOperators,
  };
}

export function makeWallDescriptor({
  level,
  docRooms,
  leftWallRoom,
  leftWallFacades,
  leftFloorAssembly,
  rightWallRoom,
  rightWallFacades,
  rightFloorAssembly,
  halfEdgeIndex,
  foundationSettings,
  foundationPatterns,
  foundationAssemblies,
  foundationComponents,
  foundationAssemblyOverrides,
  framingComponents,
  slabComponents,
  sheathingComponents,
  wallSettings,
  datumAbstractions,
  facadeSpanStratum,
  facadeSweepStratum,
  wallAssemblyOperators,
}: {
  level: Level;
  docRooms: Table<StaticRoom>;
  leftWallRoom: WallSideRoomDescriptor;
  leftWallFacades: Facade[];
  leftFloorAssembly: FloorAssembly;
  rightWallRoom: WallSideRoomDescriptor;
  rightWallFacades: Facade[];
  rightFloorAssembly: FloorAssembly;
  halfEdgeIndex: number;
  foundationSettings: FoundationSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationPatterns: HalfEdgeAssociationTable<FoundationPattern>;
  foundationAssemblyOverrides: HalfEdgeAssociationTable<FoundationAssemblyOverrides>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  wallSettings: WallSettings;
  datumAbstractions: Table<DatumAbstraction>;
  facadeSpanStratum: Table<FacadeSpanStratum>;
  facadeSweepStratum: Table<FacadeSweepStratum>;
  wallAssemblyOperators: HalfEdgeAssociationTable<WallAssemblyOperator>;
}): WallDescriptor {
  /**
   * @NOTE - right now we are doing this for every half edge, but they are the same collection. We should
   * precompute this and pass both this and the foundation datums as separate arguments to the function.
   */
  const wallDatums = collectAllWallDatums(datumAbstractions);
  const { componentInstanceChain } = level;

  const wallOperators = collectHalfEdgeOperators(
    halfEdgeIndex,
    leftWallRoom,
    rightWallRoom,
    wallAssemblyOperators,
    leftWallFacades,
    rightWallFacades,
    wallDatums,
    facadeSpanStratum,
    facadeSweepStratum,
    componentInstanceChain,
  );

  const { room: leftRoom, roomClass: leftRoomClass } = leftWallRoom;
  const { room: rightRoom, roomClass: rightRoomClass } = rightWallRoom;
  const floorPriority = floorPathPriority({
    leftWallRoom,
    rightWallRoom,
    leftFloorAssembly,
    rightFloorAssembly,
    foundationSettings,
  });

  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const wallTop = level.topOfWall; // // TODO - may be assigned by ceiling assembly in the future

  /**
   * @TODO - disabled until wall top offset is implemented
   */
  // // Simple largest wins heuristic for now
  // let wallTopOffset: number | null = null;
  // const leftWallTopOperators = wallOperators.leftCoreOperators.filter(isWallOperatorSetTopDatum);
  // if (leftWallTopOperators.length > 0) {
  //   const offset = leftWallTopOperators[leftWallTopOperators.length - 1].offset;
  //   wallTopOffset = wallTopOffset !== null ? Math.max(wallTopOffset, offset) : offset;
  // }
  // const rightWallTopOperators = wallOperators.rightCoreOperators.filter(isWallOperatorSetTopDatum);
  // if (rightWallTopOperators.length > 0) {
  //   const offset = rightWallTopOperators[rightWallTopOperators.length - 1].offset;
  //   wallTopOffset = wallTopOffset !== null ? Math.max(wallTopOffset, offset) : offset;
  // }

  // const wallTop = wallTopOffset !== null ? levelInchesNew(level.topOfWall + wallTopOffset) : level.topOfWall;

  const floorPathPartial = { leftFloorAssembly, rightFloorAssembly, floorPriority };
  const floorPathTop = floorPathBaseOffset(floorPathPartial);

  const leftCeilingBottom = level.topOfWall + (leftRoom?.ceilingHeightOffset ?? 0);
  const leftFloorVoid = leftFloorAssembly.assemblyType === FloorAssemblyType.VOID;

  let leftFloorStructuralType = StructuralFloorType.FRAMING;
  let leftFloorTop = floorAssemblyBaseOffset(leftFloorAssembly);
  let leftFloorAssemblyThickness = floorAssemblyFloorPlateThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    leftFloorAssembly,
  );
  let leftFloorBottom = leftFloorTop - leftFloorAssemblyThickness;
  let leftFoundationTop = leftFloorBottom;
  let leftFoundationBottom = leftFoundationTop;

  const rightCeilingBottom = level.topOfWall + (rightRoom?.ceilingHeightOffset ?? 0);
  const rightFloorVoid = rightFloorAssembly.assemblyType === FloorAssemblyType.VOID;

  let rightFloorStructuralType = StructuralFloorType.FRAMING;
  let rightFloorTop = floorAssemblyBaseOffset(rightFloorAssembly);
  let rightFloorAssemblyThickness = floorAssemblyFloorPlateThickness(
    framingComponents,
    slabComponents,
    sheathingComponents,
    rightFloorAssembly,
  );
  let rightFloorBottom = rightFloorTop - rightFloorAssemblyThickness;
  let rightFoundationTop = rightFloorBottom;
  let rightFoundationBottom = rightFoundationTop;

  if (level.floorIndex <= 0) {
    /**
     * This is a placeholder block until slabs are migrated into floor assemblies.
     * Right now we need to convert foundation settings information to mimic the behavior of
     * floor assemblies. Keeping this in a separate statement because it is likely to
     * change, while the other foundation calculations will probably remain mostly stable
     */

    const leftRoomClass = leftRoom?.attributes.roomClass ?? RoomClass.EXTERIOR;
    assertIsNotUndefined(foundationSystemSettings);

    const leftSlabType = slabTypeAtRoomClass(leftRoomClass, foundationSystemSettings);

    if (leftSlabType !== SlabType.NONE) {
      leftFloorStructuralType = StructuralFloorType.SLAB;
      leftFloorAssemblyThickness = slabDepthAtRoomClass(leftRoomClass, foundationSystemSettings);
      leftFloorBottom = leftFloorTop - leftFloorAssemblyThickness;
      leftFoundationTop = leftFloorTop; /** @NOTE - foundation top is now top of floor */
      leftFoundationBottom = leftFloorBottom; /** @NOTE - foundation bottom is now bottom of floor */
    }

    const rightRoomClass = rightRoom?.attributes.roomClass ?? RoomClass.EXTERIOR;
    const rightSlabType = slabTypeAtRoomClass(rightRoomClass, foundationSystemSettings);

    if (rightSlabType !== SlabType.NONE) {
      rightFloorStructuralType = StructuralFloorType.SLAB;
      rightFloorAssemblyThickness = slabDepthAtRoomClass(rightRoomClass, foundationSystemSettings);
      rightFloorBottom = rightFloorTop - rightFloorAssemblyThickness;
      rightFoundationTop = rightFloorTop; /** @NOTE - foundation top is now top of floor */
      rightFoundationBottom = rightFloorBottom; /** @NOTE - foundation top is now bottom of floor */
    }
  }

  // align void floors with opposite floor assemblies
  if (leftFloorVoid !== rightFloorVoid) {
    if (leftFloorVoid) {
      leftFloorStructuralType = rightFloorStructuralType;
      leftFloorTop = rightFloorTop;
      leftFloorAssemblyThickness = rightFloorAssemblyThickness;
      leftFloorBottom = rightFloorBottom;
      leftFoundationTop = rightFoundationTop;
      leftFoundationBottom = rightFoundationBottom;
    } else {
      rightFloorStructuralType = leftFloorStructuralType;
      rightFloorTop = leftFloorTop;
      rightFloorAssemblyThickness = leftFloorAssemblyThickness;
      rightFloorBottom = leftFloorBottom;
      rightFoundationTop = leftFoundationTop;
      rightFoundationBottom = leftFoundationBottom;
    }
  }

  let isFurring = false;
  let isCripple = false;
  const {
    foundationCondition,
    foundationAssembly: foundationAssemblyFromWallPath,
    foundationAssemblyComponents,
  } = getWallPathFoundationAssemblyInfo({
    level,
    docRooms,
    leftWallRoom,
    rightWallRoom,
    leftFloorAssembly,
    rightFloorAssembly,
    floorPriority,
    halfEdgeIndex,
    foundationSettings,
    foundationComponents,
    foundationAssemblies,
    foundationAssemblyOverrides,
    foundationPatterns,
    framingComponents,
    slabComponents,
    sheathingComponents,
    datumAbstractions,
  });

  const foundationAssembly = foundationAssemblyFromWallPath;

  // adjust foundation and floor
  if (level.floorIndex > 0) {
    // if both floors are void, we set the top of floor to bottom of level floorplate to cover caps
    if (leftFloorVoid && rightFloorVoid) {
      leftFloorTop = levelInchesNew(leftFloorTop - level.floorPlateThickness);
      leftFloorBottom = leftFloorTop;
      rightFloorTop = levelInchesNew(rightFloorTop - level.floorPlateThickness);
      rightFloorBottom = rightFloorTop;
    }

    const foundationTop = Math.min(leftFloorBottom, rightFloorBottom);
    leftFoundationTop = foundationTop;
    rightFoundationTop = foundationTop;

    leftFoundationBottom = leftFoundationTop;
    rightFoundationBottom = rightFoundationTop;
  } else if (foundationAssembly.assemblyType !== FoundationAssemblyType.VOID) {
    const foundationPathPartial = {
      condition: foundationCondition,
      datums: getFoundationPathDatums(
        { condition: foundationCondition },
        { floorPriority, leftFloorAssembly, rightFloorAssembly },
        level,
        framingComponents,
        slabComponents,
        sheathingComponents,
        foundationSystemSettings,
      ),
    };
    const floorPathPartial = {
      floorPriority,
      leftRoomClass,
      rightRoomClass,
      leftFloorAssembly,
      rightFloorAssembly,
    };

    assertIsNotUndefined(foundationSystemSettings);
    const foundationZ = foundationAssemblyTop({
      foundationPath: foundationPathPartial,
      floorPath: floorPathPartial,
      foundationAssembly,
      foundationSettings: foundationSystemSettings,
      foundationComponents,
      framingComponents,
      slabComponents,
      sheathingComponents,
      level,
    });

    // the floor priority decides which of the foundations/floors on either side of a half edge
    // host the wall above it
    const isFoundationLeft = floorPriority === PathPriority.LEFT;
    const foundationTop = worldInchesTolevel(foundationZ, level.floorElevation);

    const { curb } = foundationAssemblyComponents;

    isFurring = foundationTop >= wallTop;
    isCripple = !isFurring && !curb && foundationTop > Math.max(0, floorPathTop);

    if (isFurring) {
      // only the outer side adjusted
      leftFoundationTop = isFoundationLeft ? leftFoundationTop : foundationTop;
      rightFoundationTop = !isFoundationLeft ? rightFoundationTop : foundationTop;
    } else {
      // both sides adjusted
      leftFoundationTop = foundationTop;
      rightFoundationTop = foundationTop;
    }

    leftFoundationBottom = level.levelDatumsGenerated.foundation;
    rightFoundationBottom = level.levelDatumsGenerated.foundation;
  }

  const makeWallSideDatums = (isLeft: boolean): WallSideGeneratedDatums => {
    const foundationBottom = levelInchesNew(isLeft ? leftFoundationBottom : rightFoundationBottom);
    const foundationTop = levelInchesNew(isLeft ? leftFoundationTop : rightFoundationTop);
    const floorBottom = levelInchesNew(isLeft ? leftFloorBottom : rightFloorBottom);
    const floorTop = levelInchesNew(isLeft ? leftFloorTop : rightFloorTop);

    const ceilingBottom = levelInchesNew(isLeft ? leftCeilingBottom : rightCeilingBottom);
    const ceilingTop = levelInchesNew(Math.max(ceilingBottom, level.topOfWall));

    return {
      foundationBottom,
      foundationTop,
      floorBottom,
      floorTop,
      ceilingBottom,
      ceilingTop,
      wallTop,
      roofBottom: level.levelDatumsGenerated.roofBottom,
      roofTop: level.levelDatumsGenerated.roofTop,
    };
  };

  const makeWallSide = (isLeft: boolean): WallSideDescriptor => {
    const wallSideDatums = makeWallSideDatums(isLeft);

    // floor information
    const floorAssemblyType = isLeft ? leftFloorAssembly.assemblyType : rightFloorAssembly.assemblyType;
    const floorStructuralType = isLeft ? leftFloorStructuralType : rightFloorStructuralType;

    // wall information
    const wallRoom = isLeft ? leftWallRoom : rightWallRoom;
    const wallExteriorMaterial = wallSettings.exteriorWallMaterial;

    const wallSideFacades = isLeft ? leftWallFacades : rightWallFacades;

    const wallFacadeSpans: FacadeSpanStratum[] = [];
    const wallFacadeSweeps: FacadeSweepStratum[] = [];

    for (const facade of wallSideFacades) {
      const { id: facadeId } = facade;

      wallFacadeSpans.push(...docEntityGetSortedChildrenFromTable(facadeSpanStratum, facadeId));
      wallFacadeSweeps.push(...docEntityGetSortedChildrenFromTable(facadeSweepStratum, facadeId));
    }

    const wallFacadeDatums = collectWallFacadeDatums(
      datumAbstractions,
      wallFacadeSpans,
      wallFacadeSweeps,
      componentInstanceChain,
    );

    return {
      wallSideDatums,
      wallFacadeSpans,
      wallFacadeSweeps,
      wallFacadeDatums,
      wallRoom,
      wallExteriorMaterial,
      floorAssemblyType,
      floorStructuralType,
    };
  };

  return {
    leftSide: makeWallSide(true),
    rightSide: makeWallSide(false),
    floorPriority,
    isCripple,
    isFurring,
    foundationCondition,
    foundationAssembly,
    wallOperators,
  };
}
/**
 * Void wall sections are vertical sections of a wall that should not generate WallMasses because they
 * represent voids (either because of an opening, or because a portion of wall just doesn't exist below/above a threshold).
 * We generate these sections in order to compute what a wall would do if it were solid at that location, but ignore them
 * then creating the wall masses.
 */
export function isVoidWallSection(wallDescriptor: WallDescriptor, wallSection: WallSection) {
  const { leftSide, rightSide, foundationAssembly } = wallDescriptor;

  const cutPlane = levelInchesNew((wallSection.bottomOffset + wallSection.topOffset) / 2);

  const leftWallRoom = leftSide.wallRoom;
  const leftRoomEnclosure = leftWallRoom.roomEnclosure;
  const leftRoom = leftWallRoom.room;
  const rightWallRoom = rightSide.wallRoom;
  const rightRoomEnclosure = rightWallRoom.roomEnclosure;
  const rightRoom = rightWallRoom.room;

  const leftRoomBoundary = leftWallRoom.room === undefined;
  const rightRoomBoundary = rightWallRoom.room === undefined;

  const leftFloorOpen = leftRoom && roomOpenToBelow(leftRoom);
  const rightFloorOpen = rightRoom && roomOpenToBelow(rightRoom);

  const leftRoomUnwalled = !isWalledRoomEnclosure[leftRoomEnclosure];
  const rightRoomUnwalled = !isWalledRoomEnclosure[rightRoomEnclosure];

  // easy out here - all section of this wall are open
  // TODO - this needs to be revisited later. users may want to add finishes to
  // things like porches/patio slabs
  if (leftRoomUnwalled && rightRoomUnwalled) {
    return true;
  }

  const aRoomConnection = leftWallRoom.roomConnection || rightWallRoom.roomConnection;
  if (!aRoomConnection) {
    /**
     * We can ignore any walls that have no room connections - nothing here should be
     * voided
     */
    return false;
  }

  const isLeftFoundationSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, true, "FOUNDATION");
  const isRightFoundationSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, false, "FOUNDATION");
  // void between two intersecting foundation walls - right now we need to check both sides
  // because we store foundation per side rather than in the core
  const isFoundationInterSection = isLeftFoundationSection || isRightFoundationSection;
  if (isFoundationInterSection && foundationAssembly?.assemblyType !== FoundationAssemblyType.VOID) {
    return false;
  }

  const isLeftRoomSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, true, "ROOM");
  const isRightRoomSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, false, "ROOM");
  // void defined by the user as a FULL_OPENING produces a void between walkable/passage portions of the wall
  const isRoomInterSection = isLeftRoomSection && isRightRoomSection;
  if (isRoomInterSection) {
    return true;
  }

  const isLeftRoofSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, true, "ROOF");
  const isRightRoofSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, false, "ROOF");
  // void between two intersecting roof walls
  const isRoofInterSection = isLeftRoofSection && isRightRoofSection;
  if (isRoofInterSection) {
    return true;
  }

  const isLeftFloorSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, true, "FLOOR");
  const isRightFloorSection = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, false, "FLOOR");
  const isFloorInterSection = isLeftFloorSection && isRightFloorSection;
  // void between two void floors
  const aFloorConnection = leftFloorOpen && rightFloorOpen;
  if (isFloorInterSection && aFloorConnection) {
    return true;
  }

  /**
   * This check will void all sections on void walls between two half story rooms. This will maintain existing
   * behavior for half stories for the time being, although it is technically incorrect.
   * These should really be handled by clipping the wall section faces the same way
   * we do for solid walls.
   *
   * @TODO - implement correct wall face clipping on void walls.
   */
  const isLeftRoomHalfStoryOrBoundary = leftRoomBoundary || leftWallRoom.roomHalfStory;
  const isRightRoomHalfStoryOrBoundary = rightRoomBoundary || rightWallRoom.roomHalfStory;
  if (isLeftRoomHalfStoryOrBoundary && isRightRoomHalfStoryOrBoundary) {
    return true;
  }

  /**
   * Special check to maintain baselines for cases where the wall is a boundary, and the floor
   * has been voided. Again, this should be handled by domain resolution, but a general refactor
   * is needed.
   *
   * @TODO - correct this check when we have wall bottom datum support and optional
   *  wall datum domains
   */
  const isLeftBelowFloor = cutPlane < leftSide.wallSideDatums.floorTop;
  const isRightBelowFloor = cutPlane < rightSide.wallSideDatums.floorTop;
  if (isLeftBelowFloor && isRightBelowFloor) {
    const leftFloorVoid = leftSide.floorAssemblyType === FloorAssemblyType.VOID;
    const rightFloorVoid = rightSide.floorAssemblyType === FloorAssemblyType.VOID;
    if (leftFloorVoid && rightFloorVoid) {
      return true;
    }
  }

  return false;
}

export function makeWallCoreSections(wallDescriptor: WallDescriptor) {
  const { leftSide, rightSide } = wallDescriptor;

  const wallSections: WallSection[] = [];
  const wallSectionDatums: LevelInches[] = [
    ...Object.values(leftSide.wallSideDatums),
    ...Object.values(rightSide.wallSideDatums),
  ];

  // sort all unique wall datums
  const wallSectionDatumSet = Array.from(new Set(wallSectionDatums));
  wallSectionDatumSet.sort((a, b) => a - b);
  for (let i = 0; i < wallSectionDatumSet.length - 1; i++) {
    const bottomOffset = wallSectionDatumSet[i];
    const topOffset = wallSectionDatumSet[i + 1];

    const cutPlane = levelInchesNew((bottomOffset + topOffset) / 2);

    const leftStructureType = computeWallSectionStructureType(wallDescriptor, cutPlane, true);
    const leftCladdingType = computeWallSectionCladdingType(wallDescriptor, cutPlane, true);
    const leftSectionSide = wallSectionSideNew(leftStructureType, leftCladdingType);

    const rightStructureType = computeWallSectionStructureType(wallDescriptor, cutPlane, false);
    const rightCladdingType = computeWallSectionCladdingType(wallDescriptor, cutPlane, false);
    const rightSectionSide = wallSectionSideNew(rightStructureType, rightCladdingType);

    wallSections.push(wallSectionNew(leftSectionSide, rightSectionSide, bottomOffset, topOffset));
  }

  return wallSections;
}

export function makeWallFinishSections(
  wallDescriptor: WallDescriptor,
  leftFinishDatums: LevelInches[],
  rightFinishDatums: LevelInches[],
) {
  const { leftSide, rightSide } = wallDescriptor;

  const wallSections: WallSection[] = [];
  const wallSectionDatums: LevelInches[] = [
    ...Object.values(leftSide.wallSideDatums),
    ...Object.values(rightSide.wallSideDatums),
    ...leftFinishDatums,
    ...rightFinishDatums,
  ];

  const leftMinFinishDatum = leftFinishDatums.length > 0 ? levelInchesNew(Math.min(...leftFinishDatums)) : undefined;
  const leftMaxFinishDatum = leftFinishDatums.length > 0 ? levelInchesNew(Math.max(...leftFinishDatums)) : undefined;

  const rightMinFinishDatum = rightFinishDatums.length > 0 ? levelInchesNew(Math.min(...rightFinishDatums)) : undefined;
  const rightMaxFinishDatum = rightFinishDatums.length > 0 ? levelInchesNew(Math.max(...rightFinishDatums)) : undefined;

  // sort all unique wall datums
  const wallSectionDatumSet = Array.from(new Set(wallSectionDatums));
  wallSectionDatumSet.sort((a, b) => a - b);
  for (let i = 0; i < wallSectionDatumSet.length - 1; i++) {
    const bottomOffset = wallSectionDatumSet[i];
    const topOffset = wallSectionDatumSet[i + 1];

    const cutPlane = levelInchesNew((bottomOffset + topOffset) / 2);

    const leftStructureType = computeWallSectionStructureType(wallDescriptor, cutPlane, true);
    const leftCladdingType = computeWallSectionCladdingType(
      wallDescriptor,
      cutPlane,
      true,
      leftMinFinishDatum,
      leftMaxFinishDatum,
    );
    const leftSectionSide = wallSectionSideNew(leftStructureType, leftCladdingType);

    const rightStructureType = computeWallSectionStructureType(wallDescriptor, cutPlane, false);
    const rightCladdingType = computeWallSectionCladdingType(
      wallDescriptor,
      cutPlane,
      false,
      rightMinFinishDatum,
      rightMaxFinishDatum,
    );
    const rightSectionSide = wallSectionSideNew(rightStructureType, rightCladdingType);

    wallSections.push(wallSectionNew(leftSectionSide, rightSectionSide, bottomOffset, topOffset));
  }

  return wallSections;
}

/**
 * Computes the structure type for a wall section based on the cut plane and the wall descriptor.
 * @param wallDescriptor
 * @param cutPlane
 * @param isLeftSide
 * @returns the structure type for the wall section
 */
function computeWallSectionStructureType(
  wallDescriptor: WallDescriptor,
  cutPlane: LevelInches,
  isLeftSide: boolean,
): StructuralAssemblyType {
  const { leftSide, rightSide, floorPriority, isFurring } = wallDescriptor;
  const wallSide = isLeftSide ? leftSide : rightSide;
  const { wallRoom, wallSideDatums } = wallSide;

  const isPrioritySide = floorPriority === PathPriority.LEFT ? isLeftSide : !isLeftSide;

  const floorStructure = wallSide.floorStructuralType;
  const floorType = wallSide.floorAssemblyType;

  const { room, roomEnclosure } = wallRoom;
  const roomOpenBelow = (room && roomOpenToBelow(room)) || roomEnclosure === RoomEnclosure.NONE; // @TODO - revisit roomEnclosure - KBMs will not be 100%

  const inFoundation = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "FOUNDATION");
  const inFloor = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "FLOOR");
  const inCeiling = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "CEILING");
  const inRoof = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "ROOF");
  const inRoom = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "ROOM");

  if (inFoundation) {
    /**
     * @NOTE - mimic legacy behavior for basement walls. This should be replaced
     * with multiple domain resolution through an updateed WallSection interface
     *
   ```
                                                 FURRING WALL  
  foundation/wall top -------------------------- +------+
                                              |XX|      |       
                  STRUCTURE TYPE WALL ------> |XX|      | <----- STRUCTURE TYPE FOUNDATION          
                                              |XX|      |                                                       
floor top/wall bottom ------- +-----------------+|      |             
                              |  slab           ||   <------ stem wall                                        
         floor bottom --------+-----------------+|      |   
                                                 |      |   
    foundation bottom ---------------------------+------+
  ```
     */
    const isFurringWallSection = inRoom && isFurring && isPrioritySide;
    if (!isFurringWallSection) {
      return StructuralAssemblyType.FOUNDATION;
    }
  }

  if (inFloor) {
    /** 
   ```
   platform floor - structure here is FLOOR  
                                          WALL SIDE                                   
                                                 |  |             
                                                 +--+                                                          
            floor top --------------- +-------------+                
                                      |  floor      |                                                  
         floor bottom --------------- +-------------+  
                                                 +--+                                                            
                                                 |  |   
   balloon/void floor - structure here is WALL                                                    
                                                 |  |                                                       
            floor top --------------- +---------+|  |             
                                      |  floor  ||  |                                                  
         floor bottom --------------- +---------+|  |   
                                                 |  |   


  ```
   */
    const floorOpen = floorType === FloorAssemblyType.VOID && floorStructure === StructuralFloorType.FRAMING;
    const floorExposed = roomOpenBelow || floorOpen;
    const floorBalloon = !isPrioritySide || floorExposed;
    if (!floorBalloon) {
      return StructuralAssemblyType.FLOOR;
    }
  }

  if (inCeiling) {
    /** 
   ```
  because ceilings are always balloon on both sides for now, the structure is always WALL. this may change
                                                                         
                                          WALL SIDE
          ceiling top --------------- +---------++--+                                                          
                                      | ceiling ||  |           
       ceiling bottom --------------- +---------+|  |                                            
                                                 |  |   
  ```
   */
    const ceilingBalloon = true;
    if (!ceilingBalloon) {
      return StructuralAssemblyType.CEILING;
    }
  }

  if (inRoof) {
    /**
     * @TODO - This should be StructuralAssemblyType.ROOF in the future but
     * it breaks baselines, so this is always WALL
     */
    return StructuralAssemblyType.WALL;
  }

  /**
   * @TODO - this is a temporary fix until we enable wall top/bottom datum
   * support.
   */
  const minDomainDatum = Math.min(...Object.values(wallSideDatums));
  const maxDomainDatum = Math.max(...Object.values(wallSideDatums));
  if (cutPlane < minDomainDatum || cutPlane > maxDomainDatum) {
    return StructuralAssemblyType.NONE;
  }

  return StructuralAssemblyType.WALL;
}

/**
 * Computes the cladding type for a wall section based on the cut plane and the wall descriptor.
 * @param wallDescriptor
 * @param cutPlane
 * @param isLeftSide
 * @param minFinishDatum
 * @returns the cladding type for the wall section
 */
function computeWallSectionCladdingType(
  wallDescriptor: WallDescriptor,
  cutPlane: LevelInches,
  isLeftSide: boolean,
  minFinishDatum?: LevelInches,
  maxFinishDatum?: LevelInches,
): WallCladdingType {
  const { leftSide, rightSide, floorPriority, isFurring } = wallDescriptor;
  const wallSide = isLeftSide ? leftSide : rightSide;
  const { wallRoom, wallSideDatums } = wallSide;

  const isPrioritySide = floorPriority === PathPriority.LEFT ? isLeftSide : !isLeftSide;
  const isBelowMaxFinishDatum = maxFinishDatum !== undefined && maxFinishDatum > cutPlane;
  const isAboveMinFinishDatum = minFinishDatum !== undefined && minFinishDatum < cutPlane;

  const floorStructure = wallSide.floorStructuralType;
  const floorType = wallSide.floorAssemblyType;

  const { room, roomEnclosure } = wallRoom;

  const roomInterior = isInteriorRoomEnclosure[roomEnclosure];
  const roomCladdingType = roomInterior ? WallCladdingType.INTERIOR : WallCladdingType.EXTERIOR;

  const inFoundation = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "FOUNDATION");
  const inFloor = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "FLOOR");
  const inCeiling = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "CEILING");
  const inRoof = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "ROOF");
  const inRoom = cutPlaneOverlapsWallSideDomain(wallDescriptor, cutPlane, isLeftSide, "ROOM");

  if (inFoundation) {
    /**
     * Cladding on the foundation is applied only if the foundation is exposed via a furring wall or
     * if the cut plane is above the min finish datum.
     */
    const isFurringWallSection = inRoom && isFurring && isPrioritySide;
    const foundationClad = isAboveMinFinishDatum || isFurringWallSection;
    if (!foundationClad) {
      return WallCladdingType.NONE;
    }
  }

  if (inFloor) {
    /**
     * Cladding in the floor is applied only if the floor is exposed via a void floor or
     * if the room is marked as open to below.
     *
     * @TODO - openToBelow should be deprecated.
     * @TODO - void floors should be replaced with a lack of floor domain
     */
    const roomOpenBelow = (room && roomOpenToBelow(room)) || roomEnclosure === RoomEnclosure.NONE;
    const floorOpen = floorType === FloorAssemblyType.VOID && floorStructure === StructuralFloorType.FRAMING;
    const floorExposed = roomOpenBelow || floorOpen;
    if (!floorExposed) {
      return WallCladdingType.NONE;
    }
  }

  if (inCeiling) {
    /**
     * Cladding in the ceiling is applied only if the room is marked as open to above.
     *
     * @TODO - openToAbove should be deprecated.
     * @TODO - void ceilings should be replaced with a lack of ceiling domain
     */
    const roomOpenAbove = (room && roomOpenToAbove(room)) || roomEnclosure === RoomEnclosure.NONE;
    const ceilingExposed = roomOpenAbove;
    if (!ceilingExposed) {
      return WallCladdingType.NONE;
    }
  }

  if (inRoof) {
    /**
     * Roof cladding is the same as the room cladding, but this is legacy behavior
     * that should be updated in isolation
     */
    return roomCladdingType;
  }

  const belowFloor = cutPlane < wallSideDatums.floorBottom;
  if (belowFloor) {
    /**
     * @TODO - This block should ALWAYS return EXTERIOR but we have legacy facades
     * that apply to both sides of the wall, and we need to suppress them on the the inside of the room.
     * This check is really not ideal and needs to be cleaned up
     */
    const { roomSupport } = wallRoom;
    const hasRoomBelow = roomSupport === RoomSupport.SUPPORTED;
    // this is a dirty check because we always have a floor domain even if the floor is void.
    // TODO - don't add floor domains if the floor is void.
    const hasSlabFloor = floorStructure === StructuralFloorType.SLAB && !!room;
    if (hasRoomBelow || hasSlabFloor) {
      return WallCladdingType.NONE;
    }
    return WallCladdingType.EXTERIOR;
  }

  const aboveCeiling = wallSideDatums.ceilingTop < cutPlane;
  if (aboveCeiling) {
    /**
     * @TODO - This block should ALWAYS return EXTERIOR but we have legacy facades
     * that apply to both sides of the wall, and we need to suppress them on the the inside of the room.
     * This check is really not ideal and needs to be cleaned up
     */
    const { roomCover } = wallRoom;
    const hasRoomAbove = roomCover === RoomCover.COVERED;
    // this is a dirty check to maintain existing behavior which suppresses exterior
    // walls above any room. We need to remove this when we implement wall top datum support.
    const isPerimeterWall = !room;
    if (hasRoomAbove || !isBelowMaxFinishDatum || !isPerimeterWall) {
      return WallCladdingType.NONE;
    }
    return WallCladdingType.EXTERIOR;
  }

  // in the room
  return roomCladdingType;
}

/**
 * Temporary function until domains are stored as part of the wall descriptor rather than
 * being computed on the fly.
 * @param wallDescriptor - the wall descriptor
 * @param cutPlane - the cut plane to check for overlap
 * @param isLeftSide - whether the cut plane is on the left side of the wall
 * @param domainType - the domain type to check for overlap
 * @returns true if the cut plane overlaps the domain, false otherwise
 */
function cutPlaneOverlapsWallSideDomain(
  wallDescriptor: WallDescriptor,
  cutPlane: LevelInches,
  isLeftSide: boolean,
  domainType: "FOUNDATION" | "FLOOR" | "CEILING" | "ROOF" | "ROOM",
) {
  const { leftSide, rightSide } = wallDescriptor;
  const wallSide = isLeftSide ? leftSide : rightSide;
  const { wallSideDatums } = wallSide;

  switch (domainType) {
    case "FOUNDATION":
      return wallSideDatums.foundationBottom < cutPlane && cutPlane < wallSideDatums.foundationTop;
    case "FLOOR":
      return wallSideDatums.floorBottom < cutPlane && cutPlane < wallSideDatums.floorTop;
    case "CEILING":
      return wallSideDatums.ceilingBottom < cutPlane && cutPlane < wallSideDatums.ceilingTop;
    case "ROOF":
      return wallSideDatums.roofBottom < cutPlane && cutPlane < wallSideDatums.roofTop;
    case "ROOM":
      return wallSideDatums.floorTop < cutPlane && cutPlane < wallSideDatums.ceilingBottom;
    default: {
      assertUnreachable(domainType);
    }
  }
}

/**
 * @TODO - swap out deprecated base offset (which will be removed once wall migration is complete)
 */
const USE_DEPRECATED_BASE_OFFSET = true;
/**
 * Maintain existing behavior for walls until wall migrations are complete
 * @TODO - this function should be removed once walls no longer use baseOffset
 * @param wallDescriptor
 * @deprecated
 */
export function deprecatedBaseOffset(wallDescriptor: WallDescriptor) {
  const { isFurring, floorPriority, leftSide, rightSide } = wallDescriptor;

  const { wallSideDatums: leftDatums } = leftSide;
  const { wallSideDatums: rightDatums } = rightSide;

  const isLeft = floorPriority === PathPriority.LEFT;

  const floorTop = isLeft ? leftDatums.floorTop : rightDatums.floorTop;
  const foundationTop = isLeft ? leftDatums.foundationTop : rightDatums.foundationTop;

  let baseOffset = floorTop;
  if (floorTop < foundationTop) {
    baseOffset = foundationTop;
  }

  if (isFurring) {
    baseOffset = floorTop;
  }

  return baseOffset;
}

/**
 * wall baseSection is used for positioning purposes and overall wall
 * mass generation
 */
export function makeWallBaseSection(wallDescriptor: WallDescriptor) {
  const {
    floorPriority,
    isFurring,
    leftSide: { wallSideDatums: leftDatums, wallRoom: leftWallRoom },
    rightSide: { wallSideDatums: rightDatums, wallRoom: rightWallRoom },
  } = wallDescriptor;
  const baseOffset = floorPriority === PathPriority.LEFT ? leftDatums.floorTop : rightDatums.floorTop;
  const topOffset = levelInchesNew(Math.max(leftDatums.ceilingBottom, rightDatums.ceilingBottom));

  const getWallCladding = (room?: Room) => {
    const roomInterior = room?.attributes.roomEnclosure && isInteriorRoomEnclosure[room?.attributes.roomEnclosure];
    const roomWallCladding = roomInterior ? WallCladdingType.INTERIOR : WallCladdingType.EXTERIOR;
    return roomWallCladding;
  };

  const leftCladding = getWallCladding(leftWallRoom.room);
  const rightCladding = getWallCladding(rightWallRoom.room);

  let leftStructure = StructuralAssemblyType.WALL;
  let rightStructure = StructuralAssemblyType.WALL;
  if (isFurring) {
    if (floorPriority === PathPriority.LEFT) {
      rightStructure = StructuralAssemblyType.FOUNDATION;
    } else if (floorPriority === PathPriority.RIGHT) {
      leftStructure = StructuralAssemblyType.FOUNDATION;
    }
  }

  const leftSectionSide = wallSectionSideNew(leftStructure, leftCladding);
  const rightSectionSide = wallSectionSideNew(rightStructure, rightCladding);

  const baseSection = wallSectionNew(
    leftSectionSide,
    rightSectionSide,
    USE_DEPRECATED_BASE_OFFSET ? deprecatedBaseOffset(wallDescriptor) : baseOffset,
    topOffset,
  );
  return baseSection;
}

/**
 * floor pathPriority detects which of side of the planarGraph edge
 * should be used for the outside of floor and foundation perimeters
 */
function floorPathPriority({
  leftWallRoom,
  rightWallRoom,
  leftFloorAssembly,
  rightFloorAssembly,
  foundationSettings,
}: {
  leftWallRoom: WallSideRoomDescriptor;
  rightWallRoom: WallSideRoomDescriptor;
  leftFloorAssembly: FloorAssembly;
  rightFloorAssembly: FloorAssembly;
  foundationSettings: FoundationSettings;
}): PathPriority {
  const { room: leftRoom, roomSupport: leftRoomSupport, roomClass: leftRoomClass } = leftWallRoom;
  const { room: rightRoom, roomSupport: rightRoomSupport, roomClass: rightRoomClass } = rightWallRoom;
  if (!leftRoom && !rightRoom) {
    return PathPriority.NONE;
  }

  if (!leftRoom) {
    return PathPriority.RIGHT;
  }

  if (!rightRoom) {
    return PathPriority.LEFT;
  }

  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);
  const { level } = leftRoom;

  const leftAttributes = leftRoom.attributes;
  const leftOffset = floorAssemblyBaseOffset(leftFloorAssembly);

  const rightAttributes = rightRoom.attributes;
  const rightOffset = floorAssemblyBaseOffset(rightFloorAssembly);

  // a room is supported if it has any rooms below it. We don't care
  // about edge alignment at this time
  const leftRoomSupported = leftRoomSupport === RoomSupport.SUPPORTED;
  const rightRoomSupported = rightRoomSupport === RoomSupport.SUPPORTED;

  // if both rooms are suported but one is a void floor,
  // the floor edge is to the side of the solid room
  const leftOpenToBelow = level.levelBelow && leftRoomSupported && leftAttributes.openToBelow;
  const rightOpenToBelow = level.levelBelow && rightRoomSupported && rightAttributes.openToBelow;

  const leftIsVoid = leftOpenToBelow || leftFloorAssembly.assemblyType === FloorAssemblyType.VOID;
  const rightIsVoid = rightOpenToBelow || rightFloorAssembly.assemblyType === FloorAssemblyType.VOID;

  // these will be the same for both rooms since
  // they have the same room class
  assertIsNotUndefined(foundationSystemSettings);
  const leftIsFraming =
    slabTypeAtRoomClass(leftRoomClass, foundationSystemSettings) === SlabType.NONE || level.floorIndex > 0;

  // check room class

  if (leftRoomClass > rightRoomClass) {
    return PathPriority.RIGHT;
  }

  if (leftRoomClass < rightRoomClass) {
    return PathPriority.LEFT;
  }

  // check open to below

  if (leftIsVoid && !rightIsVoid) {
    return PathPriority.RIGHT;
  }

  if (!leftIsVoid && rightIsVoid) {
    return PathPriority.LEFT;
  }

  // check supported

  // if the room on one side of the path is supported and the other is not,
  // the floor path needs to shift to the outside wall edge of the supported room
  if (rightRoomSupported && !leftRoomSupported) {
    return PathPriority.RIGHT;
  }

  if (!rightRoomSupported && leftRoomSupported) {
    return PathPriority.LEFT;
  }

  // check floor elevation offsets

  if (leftIsFraming) {
    // priority to lower floor framing
    if (leftOffset < rightOffset) {
      return PathPriority.LEFT;
    }

    if (leftOffset > rightOffset) {
      return PathPriority.RIGHT;
    }
  } else {
    // priority to higher slab
    if (leftOffset < rightOffset) {
      return PathPriority.RIGHT;
    }

    if (leftOffset > rightOffset) {
      return PathPriority.LEFT;
    }
  }

  // no priority - the floor edge is on the planar graph.
  // This can be expanded in the future if more checks are needed
  return PathPriority.NONE;
}

/**
 * @TODO - as much of this interface should be deprecated as possible as we move more information into the wall decriptor,
 * and remove floorPaths and foundationPaths which essentially hold this same information
 * This should not happen until there is a 1-1 with the actual foundation that will be generated
 */
interface WallPathFoundationAssemblyInfo {
  foundationCondition: FoundationCondition;
  foundationAssembly: FoundationAssembly;
  foundationAssemblyComponents: ReturnType<typeof buildFoundationAssemblyComponents>;
}

/**
 * @returns utility information about the foundation that will be generated below a WallPath
 * @note this information is slightly incorrect until we can accurately detect the brick ledge locations
 * which depend on wainscoting, but this can be bypassed with overrides
 *
 * @note this should all be deprecated once we can detect brick,
 * since building should generate/store a Table<FoundationAssembly> for each half edge
 *
 * This information should probably be stored in the WallDescriptor, once inaccuracies re:brick ledges
 * are resolved
 */
function getWallPathFoundationAssemblyInfo({
  level,
  docRooms,
  leftWallRoom,
  rightWallRoom,
  leftFloorAssembly,
  rightFloorAssembly,
  floorPriority,
  halfEdgeIndex,
  foundationSettings,
  foundationComponents,
  foundationAssemblies,
  foundationAssemblyOverrides,
  foundationPatterns,
  framingComponents,
  slabComponents,
  sheathingComponents,
  datumAbstractions,
}: {
  level: Level;
  docRooms: Table<StaticRoom>;
  leftWallRoom: WallSideRoomDescriptor;
  rightWallRoom: WallSideRoomDescriptor;
  floorPriority: PathPriority;
  leftFloorAssembly: FloorAssembly;
  rightFloorAssembly: FloorAssembly;
  halfEdgeIndex: number;
  foundationSettings: FoundationSettings;
  foundationComponents: Table<FoundationComponentEntity>;
  foundationAssemblies: Table<FoundationAssemblyEntity>;
  foundationAssemblyOverrides: HalfEdgeAssociationTable<FoundationAssemblyOverrides>;
  foundationPatterns: HalfEdgeAssociationTable<FoundationPattern>;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  datumAbstractions: Table<DatumAbstraction>;
}): WallPathFoundationAssemblyInfo {
  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);
  const getRoomDisabledFoundationGeneration = (room: Room | undefined) => {
    const lookupRoomId = room?.sourceRoomId ?? room?.id;
    return lookupRoomId ? docRooms[lookupRoomId]?.disableFoundationGeneration : undefined;
  };

  const foundationCondition = determineFoundationCondition(
    leftWallRoom.roomClass,
    getRoomDisabledFoundationGeneration(leftWallRoom.room ?? undefined) ?? false,
    rightWallRoom.roomClass,
    getRoomDisabledFoundationGeneration(rightWallRoom.room ?? undefined) ?? false,
  );
  const foundationPathPartial = {
    halfEdgeIndex,
    condition: foundationCondition,
    datums: getFoundationPathDatums(
      { condition: foundationCondition },
      { floorPriority, leftFloorAssembly, rightFloorAssembly },
      level,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSystemSettings,
    ),
  };
  const floorPathPartial = {
    floorPriority,
    leftWallRoom,
    leftRoom: leftWallRoom.room,
    leftRoomClass: leftWallRoom.roomClass,
    leftRoomSupport: leftWallRoom.roomSupport,
    leftFloorAssembly,
    rightWallRoom,
    rightRoom: rightWallRoom.room,
    rightRoomClass: rightWallRoom.roomClass,
    rightRoomSupport: rightWallRoom.roomSupport,
    rightFloorAssembly,
  };

  assertIsNotUndefined(foundationSystemSettings);
  const foundationAssemblyOverride = makeFoundationPathAssemblyOverride(
    floorPathPartial,
    getActiveFoundationAssemblyOverride(foundationAssemblyOverrides, halfEdgeIndex, foundationSettings.foundationHash),
    getActiveFoundationAssemblyOverride(
      foundationAssemblyOverrides,
      planarGraphAdjacentHalfEdge(halfEdgeIndex),
      foundationSettings.foundationHash,
    ),
    foundationAssemblies,
    foundationComponents,
    foundationSystemSettings,
    datumAbstractions,
  );

  const foundationAssembly = makeFoundationAssembly({
    level,
    foundationPath: foundationPathPartial,
    floorPath: floorPathPartial,
    foundationComponents,
    foundationAssemblies,
    foundationSettings: foundationSystemSettings,
    foundationPatterns,
    foundationAssemblyOverride,
    datumAbstractions,
  });

  const foundationAssemblyComponents = buildFoundationAssemblyComponents(foundationComponents, foundationAssembly);

  return {
    foundationCondition,
    foundationAssembly,
    foundationAssemblyComponents,
  };
}
